import { onetrust } from '@src/analyticsManager';

/**
 *	Analytics class which utilizes sockets to push data to server
 *	Note: socketProxy will pass to parent socket
 */
class Analytics {
  static analyticsData = {
    defaultProperties: {},
    socket: undefined,
    queuedSocketMessages: [],
  };

  /**
   * Construct instance.
   *
   * @param  Object params         Optional params which may include onReady, onMessage, onClose and onIntialized callback.
   */
  static init(params = {}) {
    Analytics.analyticsData.defaultProperties = params.defaultProperties;
    if (params.socket !== undefined) {
      Analytics.analyticsData.socket = params.socket;
    } else {
      // eslint-disable-next-line no-console
      console.error('Missing socket');
    }
  }

  static setSocket(socket) {
    Analytics.analyticsData.socket = socket;
    Analytics.clearSocketQueue();
  }

  static socketSend(data) {
    if (Analytics.analyticsData.socket) {
      Analytics.analyticsData.socket.send(data);
    } else {
      Analytics.analyticsData.queuedSocketMessages.push(data);
    }
  }

  static clearSocketQueue() {
    Analytics.analyticsData.queuedSocketMessages.forEach(msg => {
      Analytics.analyticsData.socket.send(msg);
    });
    Analytics.analyticsData.queuedSocketMessages = [];
  }

  /**
   * Set default properties
   */
  static setDefaultProperties(defaultProperties = {}) {
    Analytics.analyticsData.defaultProperties = defaultProperties;
  }

  /**
   * Get Event config params, kinesis partition, time, etc...
   */
  static getEventConfig() {
    const date = new Date();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    // The Unix epoch (or Unix time or POSIX time or Unix timestamp) is the number of seconds
    // that have elapsed since January 1, 1970 (midnight UTC/GMT)
    const timeStamp = Math.floor(Date.now() / 1000); // current time in seconds
    const timeStampMilliseconds = Date.now(); // current time in milliseconds

    const kinesisParition = `year=${year}/month=${month}/day=${day}/`;

    return {
      Timestamp: timeStamp,
      TimestampMilli: timeStampMilliseconds,
      KINESIS_PARTITION: kinesisParition,
    };
  }

  /**
   * The event and/or properties to have tracked.
   *
   * @param  string event      The event name to track.
   * @param  object properties optional properties to track.
   */
  static track(event, properties) {
    if (!onetrust.hasAnalyticsCookiesConsent()) {
      return;
    }

    // eslint-disable-next-line no-underscore-dangle
    const _properties = {
      ...Analytics.getEventConfig(),
      ...Analytics.analyticsData.defaultProperties,
      ...properties,
    };
    const analyticsData = {
      event,
      properties: _properties,
    };
    const socketData = Analytics.formatSocketAction(analyticsData);
    Analytics.socketSend(socketData);
  }

  /**
   * An array of events to have tracked.
   *
   * @param  array events The array of events to track.
   */
  static trackBatch(events) {
    if (!onetrust.hasAnalyticsCookiesConsent()) {
      return;
    }

    const socketData = Analytics.formatSocketAction(events);
    Analytics.socketSend(socketData);
  }

  /**
   * Stringifies the analytics data object.
   *
   * @param array | {} analyticsData the analytics data to send for processing
   */
  static formatSocketAction(analyticsData) {
    return JSON.stringify({
      action: 'trackAnalytics',
      data: btoa(
        JSON.stringify({
          eventType: 'track',
          eventPayload: analyticsData,
        })
      ),
    });
  }

  static bindWindowEvents() {
    // NOTE, NEEDS TESTING, SHOULD RUN WHEN BROWSER IS CLOSED //
    window.addEventListener('beforeunload', event => {
      event.preventDefault();
      return Analytics.track('browser_closed', {});
    });

    // NOTE, Needs testing, should run when user clicks back //
    window.history.pushState(
      { name: 'browserBack' },
      'on browser back click',
      window.location.href
    );
    window.addEventListener(
      'popstate',
      event => {
        if (event.state) {
          Analytics.track('back_clicked', {});
        }
      },
      false
    );
    window.addEventListener('beforeunload', event => {
      event.preventDefault();
      // if not embedded send browser closed event
      // Need to make sure url in iframe src has embedded=1 query string
      Analytics.track('browser_closed', {});
      return Analytics.track('registration_closed', {});
    });
  }
}

export default Analytics;
